import type {
    Plugin, Chart, ChartDataset,
} from 'chart.js';
import type { Ref } from 'vue';

export type LegendPluginConfigType = {
    containerRef: Ref<HTMLElement | null>;
    onDatasetToggle?: (datasetIndex: number, visible: boolean) => void;
    align?: 'start' | 'center' | 'end';
    borderRadius?: number;
    marginBottom?: number;
    marginTop?: number;
};

export const createHtmlLegendPlugin = (config: LegendPluginConfigType): Plugin => {
    const originalColors = new Map<
        number,
        {
            bg: string;
            border: string;
            pointBg?: string;
            pointBorder?: string;
        }
    >();

    const hiddenDatasets = new Set<number>();
    const COLORS = { DIMMED_COLOR: 'rgba(220, 220, 220, 0.3)' };

    const alignMap = { start: 'flex-start', center: 'center', end: 'flex-end' };
    const justifyContent = alignMap[config.align ?? 'start'];
    const borderRadius = config.borderRadius ?? 50;
    const marginBottom = config.marginBottom ?? 0;
    const marginTop = config.marginTop ?? 0;

    const getColorAsString = (color: unknown): string => {
        if (typeof color === 'string') {
            return color;
        }
        if (Array.isArray(color) && color.length > 0 && typeof color[0] === 'string') {
            return color[0];
        }

        return '#000000';
    };

    const setPointColors = (dataset: ChartDataset<'line'>, bgColor: string, borderColor: string): void => {
        if (dataset.data && dataset.data.length > 0) {
            const pointColors = {
                pointBackgroundColor: new Array(dataset.data.length).fill(bgColor),
                pointBorderColor: new Array(dataset.data.length).fill(borderColor),
            };
            Object.assign(dataset, pointColors);
        }
    };

    const updateLegend = (chart: Chart<'line'>) => {
        const container = config.containerRef.value;
        if (!container) {
            return;
        }

        const existingItems = container.querySelectorAll<HTMLElement>('.legend-item');
        const datasets = chart.data.datasets || [];

        if (existingItems.length !== datasets.length) {
            container.innerHTML = '';

            const antennasDiv = document.createElement('div');
            antennasDiv.className = 'legend';
            antennasDiv.style.cssText = `
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: center;
                    justify-content: ${justifyContent};
                    margin-bottom: ${marginBottom}px;
                    margin-top: ${marginTop}px;
                `;

            for (const [index, dataset] of datasets.entries()) {
                if (!originalColors.has(index)) {
                    originalColors.set(index, {
                        bg: getColorAsString(dataset.backgroundColor),
                        border: getColorAsString(dataset.borderColor),
                        pointBg: getColorAsString(dataset.borderColor),
                        pointBorder: getColorAsString(dataset.borderColor),
                    });
                }

                const legendItem = document.createElement('div');
                legendItem.className = 'legend-item';
                legendItem.dataset.index = index.toString();
                legendItem.style.cssText = `
                        display: flex;
                        align-items: center;
                        cursor: pointer;
                        transition: opacity 0.2s ease;
                        pointer-events: auto;
                    `;

                const boxColor = getColorAsString(dataset.backgroundColor) || getColorAsString(dataset.borderColor);
                const colorBox = document.createElement('span');
                colorBox.className = 'legend-color-box';
                colorBox.style.cssText = `
                        display: inline-block;
                        width: 12px;
                        height: 12px;
                        border-radius: ${borderRadius}%;
                        background-color: ${boxColor};
                        margin-right: 8px;
                        transition: opacity 0.2s ease;
                    `;

                const labelText = document.createElement('span');
                labelText.className = 'legend-label';
                labelText.textContent = dataset.label || '';
                labelText.style.cssText = `
                        font-size: 12px;
                        font-family: 'Roboto', sans-serif;
                        color: rgba(0, 0, 0, 0.87);
                    `;

                legendItem.addEventListener('mouseenter', () => {
                    if (hiddenDatasets.has(index)) {
                        return;
                    }

                    if (chart.data.datasets) {
                        for (const [datasetIndex, ds] of chart.data.datasets.entries()) {
                            if (hiddenDatasets.has(datasetIndex)) {
                                continue;
                            }

                            const original = originalColors.get(datasetIndex);
                            if (original) {
                                const isDimmed = datasetIndex !== index;

                                ds.backgroundColor = isDimmed ? COLORS.DIMMED_COLOR : original.bg;
                                ds.borderColor = isDimmed ? COLORS.DIMMED_COLOR : original.border;

                                const pointBgColor = isDimmed ? COLORS.DIMMED_COLOR : original.pointBg || original.bg;
                                const pointBorderColor = isDimmed
                                    ? COLORS.DIMMED_COLOR
                                    : original.pointBorder || original.border;
                                setPointColors(ds, pointBgColor, pointBorderColor);
                            }
                        }
                    }
                    chart.update('none');
                });

                legendItem.addEventListener('mouseleave', () => {
                    if (chart.data.datasets) {
                        for (const [datasetIndex, ds] of chart.data.datasets.entries()) {
                            const original = originalColors.get(datasetIndex);
                            if (original) {
                                ds.backgroundColor = original.bg;
                                ds.borderColor = original.border;
                                const pointBgColor = original.pointBg || original.bg;
                                const pointBorderColor = original.pointBorder || original.border;
                                setPointColors(ds, pointBgColor, pointBorderColor);
                            }
                        }
                    }

                    chart.update('none');
                });

                legendItem.addEventListener('click', () => {
                    // Restore all colors first (in case we're in hover state)
                    if (chart.data.datasets) {
                        for (const [datasetIndex, ds] of chart.data.datasets.entries()) {
                            const original = originalColors.get(datasetIndex);
                            if (original) {
                                ds.backgroundColor = original.bg;
                                ds.borderColor = original.border;
                            }
                        }
                    }

                    const isCurrentlyVisible = chart.isDatasetVisible(index);
                    const newVisibility = !isCurrentlyVisible;

                    chart.setDatasetVisibility(index, newVisibility);

                    if (newVisibility) {
                        hiddenDatasets.delete(index);
                    } else {
                        hiddenDatasets.add(index);
                    }

                    legendItem.style.opacity = newVisibility ? '1' : '0.5';
                    labelText.style.textDecoration = newVisibility ? 'none' : 'line-through';
                    colorBox.style.opacity = newVisibility ? '1' : '0.4';

                    config.onDatasetToggle?.(index, newVisibility);
                    chart.update();
                });

                legendItem.appendChild(colorBox);
                legendItem.appendChild(labelText);
                antennasDiv.appendChild(legendItem);
            }

            container.appendChild(antennasDiv);
        } else {
            for (const [index, item] of Array.from(existingItems).entries()) {
                const legendItem = item;
                const labelText = legendItem.querySelector<HTMLElement>('.legend-label');
                const colorBox = legendItem.querySelector<HTMLElement>('.legend-color-box');
                const isVisible = chart.isDatasetVisible(index);

                legendItem.style.opacity = isVisible ? '1' : '0.5';
                if (labelText) {
                    labelText.style.textDecoration = isVisible ? 'none' : 'line-through';
                }
                if (colorBox) {
                    colorBox.style.opacity = isVisible ? '1' : '0.4';
                }
            }
        }
    };

    return {
        id: 'htmlLegend',
        afterUpdate: updateLegend,
    };
};
